/* eslint-disable @typescript-eslint/naming-convention */
import request from '../request';

import {
  BankDetails,
  GetGuestDamage,
  GetGuestDamageListParams,
  GetGuestDamageNegotiation,
  GetGuestDamageNegotiationParams,
  GetGuestDamageNegotiationsParams,
  GetGuestDamageNegotiationsPaymentStatusCountParams,
  GetGuestDamageNegotiationsPaymentStatusCountResponse,
  GetGuestDamageNegotiationsResponse,
  GetReservationsListParams,
  GetReservationsListResponse,
  GuestDamageEditRelease,
  GuestDamageEvidence,
  GuestDamageFranchiseActivity,
  GuestDamageListItem,
  HouseHoldLinen,
  PatchGuestDamage,
  PatchGuestDamageNegotiation,
  PostDamageNegotiationGuestPayment,
  PostDamageNegotiationOwnerPayment,
  PostGuestDamage,
  PostGuestDamageNegotiation,
  PostGuestDamageNegotiationHistory,
  PropertiesList,
  ReservationDetails,
  ReservationDetailsParams,
} from './types';

export const getPropertiesList = async (): Promise<PropertiesList[]> => {
  const { data } = await request.get('/properties/properties_list');
  return data;
};

export const getGuestDamageNegotiation = async (
  params?: GetGuestDamageNegotiationParams,
): Promise<GetGuestDamageNegotiation[]> => {
  if (params) {
    const { data } = await request.get<GetGuestDamageNegotiation[]>('/guest_damage_negotiation/', {
      params,
    });
    return data;
  }

  const { data } = await request.get<GetGuestDamageNegotiation[]>('/guest_damage_negotiation/');
  return data;
};

export const postGuestDamageNegotiation = async (
  guestDamageNegotiation: PostGuestDamageNegotiation,
) => {
  const { data } = await request.post<GetGuestDamageNegotiation>('/guest_damage_negotiation/', {
    ...guestDamageNegotiation,
  } as PostGuestDamageNegotiation);
  return data;
};

export const getReservation = async (
  params?: ReservationDetailsParams,
): Promise<ReservationDetails[]> => {
  if (params) {
    const { check_in_date, check_out_date, guest_name, ota_name, property_code } = params;

    const { data } = await request.get<ReservationDetails[]>('/reservation/', {
      params: {
        check_in_date,
        check_out_date,
        search: guest_name,
        ota_name,
        property_code,
      },
    });
    return data;
  }

  const { data } = await request.get<ReservationDetails[]>('/reservation/');
  return data;
};

export const postGuestDamage = async (guestDamage?: PostGuestDamage): Promise<PostGuestDamage> => {
  const { data } = await request.post<PostGuestDamage>('/guest_damage/', {
    ...guestDamage,
  });
  return data;
};

export const getGuestDamageList = async (
  params?: GetGuestDamageListParams,
): Promise<GuestDamageListItem[]> => {
  const { data } = await request.get<GuestDamageListItem[]>('/guest_damage/', {
    params,
  });
  return data;
};

export const postGuestDamageEvidences = async (guestDamageId: number, evidence?: string) => {
  await request.post<GuestDamageEvidence>('/guest_damage_evidence/', {
    evidence,
    guest_damage: guestDamageId,
  });
};

export const getReservationGuestDamages = async (reservationId: number) => {
  const { data } = await request.get<GetGuestDamage[]>(
    `/reservation/${reservationId}/guest_damages/`,
  );
  return data;
};

export const patchGuestDamage = async (
  id: number,
  guestDamage?: PatchGuestDamage,
): Promise<GetGuestDamage> => {
  const { data } = await request.patch<GetGuestDamage>(`/guest_damage/${id}/`, {
    ...guestDamage,
  });
  return data;
};

export const patchGuestDamageNegotiation = async (
  id: number,
  guestDamageNegotiation?: PatchGuestDamageNegotiation,
): Promise<PatchGuestDamageNegotiation> => {
  const { data } = await request.patch<PatchGuestDamageNegotiation>(
    `/guest_damage_negotiation/${id}/`,
    guestDamageNegotiation,
  );
  return data;
};

export const postDamageNegotiationGuestPayment = async (
  guestDamageNegotiationId: number,
  guestPaymentReceipt: string,
) => {
  const { data } = await request.post<PostDamageNegotiationGuestPayment>(
    '/damage_negotiation_guest_payment/',
    {
      guest_damage_negotiation: guestDamageNegotiationId,
      guest_payment_receipt: guestPaymentReceipt,
    } as PostDamageNegotiationGuestPayment,
  );
  return data;
};

export const getGuestDamageNegotiationById = async (
  id: number,
): Promise<GetGuestDamageNegotiation> => {
  const { data } = await request.get<GetGuestDamageNegotiation>(`/guest_damage_negotiation/${id}`);
  return data;
};

// Releases the launch edit to the franchise (201). 409 outside Validação–charges or
// when it is already released (ADR 131 in sapron-backend).
export const postGuestDamageEditRelease = async (id: number): Promise<GuestDamageEditRelease> => {
  const { data } = await request.post<GuestDamageEditRelease>(
    `/guest_damage_negotiation/${id}/edit-release/`,
  );
  return data;
};

// The backoffice opened the damage: saved edits stop marking the kanban card (204).
export const postGuestDamageEditReleaseSeen = async (id: number): Promise<void> => {
  await request.post(`/guest_damage_negotiation/${id}/edit-release/seen/`);
};

// Revokes the open release before the franchise saves (200). 409 without one.
export const postGuestDamageEditReleaseRevoke = async (
  id: number,
): Promise<GuestDamageEditRelease> => {
  const { data } = await request.post<GuestDamageEditRelease>(
    `/guest_damage_negotiation/${id}/edit-release/revoke/`,
  );
  return data;
};

// Deletes the launch: the negotiation and every damage of its reservation (204).
// 409 when it left Solicitado meanwhile (ADR 121 in sapron-backend).
export const deleteGuestDamageNegotiation = async (id: number): Promise<void> => {
  await request.delete(`/guest_damage_negotiation/${id}/`);
};

// Triggers the franchise by hand (Validação without a live chamado, after the
// Central failed when the phase changed): opens the damage's chamado with `motivo`
// as its first message.
export const postGuestDamageFranchiseActivity = async (
  negotiationId: number,
  motivo: string,
): Promise<GuestDamageFranchiseActivity> => {
  const { data } = await request.post<GuestDamageFranchiseActivity>(
    `/guest_damage_negotiation/${negotiationId}/franchise-activity/`,
    { motivo },
  );
  return data;
};

export const getGuestDamageById = async (id: number): Promise<GetGuestDamage> => {
  const { data } = await request.get<GetGuestDamage>(`/guest_damage/${id}`);
  return data;
};

export const postGuestDamageNegotiationHistory = async (
  guestDamageNegotiationId: number,
  guestDamageNegotiationHistory: PostGuestDamageNegotiationHistory,
) => {
  const { data } = await request.post<PostGuestDamageNegotiationHistory>(
    '/guest_damage_negotiation_history/',
    {
      ...guestDamageNegotiationHistory,
      guest_damage_negotiation: guestDamageNegotiationId,
    },
  );
  return data;
};

export const getOwnerBankDetailsById = async (id: number): Promise<BankDetails[]> => {
  const { data } = await request.get<BankDetails[]>('/financial/owner/bank_details/', {
    params: {
      user_id: id,
    },
  });
  return data;
};

export const postDamageNegotiationOwnerPayment = async (
  ownerDamageNegotiationId: number,
  ownerPaymentReceipt: string,
) => {
  const { data } = await request.post<PostDamageNegotiationOwnerPayment>(
    '/damage_negotiation_owner_payment/',
    {
      guest_damage_negotiation: ownerDamageNegotiationId,
      owner_payment_receipt: ownerPaymentReceipt,
    } as PostDamageNegotiationOwnerPayment,
  );
  return data;
};

export const getReservationGuestDamageNegotiation = async (reservationId: number) => {
  const { data } = await request.get<GetGuestDamageNegotiation>(
    `/reservation/${reservationId}/guest_damage_negotiation/`,
  );
  return data;
};

export const getReservationById = async (id: number): Promise<BankDetails[]> => {
  const { data } = await request.get<BankDetails[]>(`/reservation/${id}/`);
  return data;
};

export const deleteGuestDamageEvidence = async (id: number) => {
  await request.delete(`/guest_damage_evidence/${id}`);
};

export const getHouseHoldLinen = async () => {
  const { data } = await request.get<HouseHoldLinen[]>('/household_linen/');
  return data;
};

export const getGuestDamageNegotiations = async (
  params?: GetGuestDamageNegotiationsParams,
): Promise<GetGuestDamageNegotiationsResponse> => {
  const { data } = await request.get<GetGuestDamageNegotiationsResponse>(
    '/guest_damages/negotiations/',
    params ? { params } : undefined,
  );
  return data;
};

export const getGuestDamageNegotiationsPaymentStatusCount = async (
  params?: GetGuestDamageNegotiationsPaymentStatusCountParams,
): Promise<GetGuestDamageNegotiationsPaymentStatusCountResponse> => {
  const { data } = await request.get<GetGuestDamageNegotiationsPaymentStatusCountResponse>(
    '/guest_damages/negotiations/payment-status-count/',
    params ? { params } : undefined,
  );
  return data;
};

export const getReservationsList = async (
  params?: GetReservationsListParams,
): Promise<GetReservationsListResponse> => {
  const { data } = await request.get<GetReservationsListResponse>(
    '/reservations/list/',
    params ? { params } : undefined,
  );
  return data;
};
